import React, { useState, useEffect, useRef } from 'react';
import { getEmployeeTypeDisplayName, bulkImportShifts, BulkImportShiftsResult, wipeShifts } from '../services/api.ts';
import { utcToLocalTime } from '../utils/timezone.ts';
import { useEmployees, useHospitals, useShiftsForDay } from '../data/queries.ts';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import { compareShiftRowsByTypeThenEnd, paneRowSortKey } from './boardViewHelpers.ts';
import StaffingGrid from './StaffingGrid.tsx';
import {
  crnaNameplateColor, doctorBullpenColor, studentColorFor, doctorPlateStyle, crnaPlateStyle,
} from './staffNameplates.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

interface ShiftsCardProps {
  selectedDate: string;
  hasPersistedAllocations: boolean;
  showSearch?: boolean;
  showUpload?: boolean; // Control whether to show the Upload Spreadsheet button
  // Optional: when provided (Board usage), filter shifts to those covering this instant
  targetTimeUtc?: string;
  // Employees currently allocated at target time
  busyDoctors?: string[];
  busyNonDoctors?: string[];
  // Key to force refresh when changed
  refreshKey?: number;
  // 'available' (default) hides shifts not covering targetTimeUtc and hides busy non-doctors;
  // 'all' shows every shift for the selected day regardless of target time / busy state.
  filterMode?: 'available' | 'all';
  // Header text. Defaults to 'Available Shifts'.
  title?: string;
  // When provided, use these shifts directly and skip the internal fetch.
  // Lets a parent (e.g. BoardView) own the shift data so multiple consumers share one fetch.
  shifts?: any[];
  // Forwarded loading flag for the parent-owned-shifts case. Ignored when `shifts` is undefined.
  loading?: boolean;
  // When true, render a destructive "Delete All Shifts" button that wipes every shift overlapping
  // the selected day. Defaults to false so reuse contexts (e.g. BoardView) never expose it.
  showDeleteAll?: boolean;
  /**
   * Manpower layout: one independently scrollable pane per staff category PRESENT ON THE DAY
   * (anesthesiologists / CRNAs / students), each name on a board-coloured plate instead of a
   * table row. A category with nobody on it draws no pane at all.
   *
   * OPT-IN, defaulting false, deliberately. BoardView reuses this card and did not ask for a split,
   * and ShiftsCard.test.tsx asserts against the single-table markup -- including visual row ORDER
   * across doctors/nurses/students, which two panes cannot satisfy. Off by default, both keep exactly
   * what they have.
   */
  splitByType?: boolean;
  /**
   * Manpower spreadsheet view: every doc and CRNA in the SYSTEM down the left, days across, stacked
   * sections rather than side-by-side panes. Takes precedence over splitByType when both are set.
   *
   * splitByType is deliberately LEFT IN PLACE rather than deleted -- the layout here has changed twice,
   * so reverting to the panes is a one-word change at the call site instead of a revert.
   */
  gridView?: boolean;
}

const ShiftsCard: React.FC<ShiftsCardProps> = ({ selectedDate, hasPersistedAllocations, showSearch = true, showUpload = true, targetTimeUtc, busyDoctors = [], busyNonDoctors = [], refreshKey, filterMode = 'available', title = 'Available Shifts', shifts: shiftsProp, loading: loadingProp, showDeleteAll = false, splitByType = false, gridView = false }) => {
  const { isAdmin } = useAuth();
  const externallyProvided = shiftsProp !== undefined;
  // Shared cache (data/queries.ts): the Board and the Shifts page already hold these, so this card adds no
  // requests of its own. When the parent passes `shifts`, the day query is disabled (date '').
  const shiftsQuery = useShiftsForDay(externallyProvided ? '' : selectedDate);
  const employees: any[] = useEmployees().data ?? [];
  const hospitals: any[] = useHospitals().data ?? [];
  const shifts = externallyProvided ? (shiftsProp as any[]) : (shiftsQuery.data ?? []);
  const isLoading = externallyProvided ? (loadingProp ?? false) : (!!selectedDate && shiftsQuery.isPending);
  useEffect(() => { if (shiftsQuery.error) console.error('Error searching shifts:', shiftsQuery.error); }, [shiftsQuery.error]);
  const [filterEmployee, setFilterEmployee] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkImportShiftsResult | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refresh shifts when refreshKey changes (e.g., after simulation). Uploads and Delete All need no manual
  // refresh: their api.ts calls mark cached shifts stale, which refetches this card and the roster grid.
  const { refetch: refetchShifts } = shiftsQuery;
  useEffect(() => {
    if (selectedDate && refreshKey !== undefined && !externallyProvided) {
      refetchShifts();
    }
  }, [refreshKey, selectedDate, refetchShifts, externallyProvided]);

  const coversTarget = (shift: any) => {
    if (!targetTimeUtc) return true;
    const t = new Date(targetTimeUtc).getTime();
    const s = new Date(shift.startTimeUtc).getTime();
    const e = new Date(shift.endTimeUtc).getTime();
    return t >= s && t < e;
  };

  const isBusyAndHidden = (shift: any) => {
    const empId = shift.employeeId as string;
    if (busyNonDoctors.includes(empId)) return true; // hide nurses and students when allocated
    return false;
  };

  const filteredShifts = shifts
    .filter(shift => filterMode === 'all' ? true : coversTarget(shift))
    .filter(shift => {
      if (filterMode !== 'all' && isBusyAndHidden(shift)) return false;
      if (!showSearch || !filterEmployee) return true;
      const employee = employees.find(emp => emp.employeeId === shift.employeeId);
      if (!employee) return false;
      const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
      return fullName.includes(filterEmployee.toLowerCase());
    })
    .sort(compareShiftRowsByTypeThenEnd);

  const formatTime = (utcTime: string) => {
    return utcToLocalTime(utcTime);
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.employeeId === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  const getEmployeeById = (employeeId: string) => {
    return employees.find(emp => emp.employeeId === employeeId);
  };

  const getHospitalName = (hospitalId: string) => {
    const hospital = hospitals.find(h => h.hospitalId === hospitalId);
    return hospital ? hospital.name : undefined;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|ods)$/i)) {
      alert('Please upload a valid Excel file (.xls, .xlsx, or .ods)');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target?.result as string;
          // Remove the data URL prefix (e.g., "data:application/vnd.ms-excel;base64,")
          const base64Content = base64Data.split(',')[1];

          // Call the API
          const result = await bulkImportShifts(base64Content);
          setUploadResult(result);

          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Error importing shifts:', error);
          alert('Failed to import shifts. Please check the console for details.');
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        alert('Failed to read the file. Please try again.');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Failed to process the file. Please try again.');
      setUploading(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteAll = async () => {
    if (!selectedDate) return;
    if (!window.confirm(`Are you sure you want to delete all shifts for ${selectedDate}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setDeleteResult(null);
    try {
      const result = await wipeShifts(selectedDate);
      setDeleteResult(result.message);
    } catch (error) {
      console.error('Error deleting shifts:', error);
      window.alert('Failed to delete shifts. Please check the console for details.');
    } finally {
      setDeleting(false);
    }
  };

  // Colour a plate the way the BOARD would colour this person's nameplate, so the two views agree.
  // Doctors: shift workers read Out 3pm/5pm/7pm, pecking docs get the supervise baseline. CRNAs read
  // their shift end (plus overnight). Students read their competency tier.
  const plateFor = (shift: any) => {
    const ns = { startTimeUtc: shift.startTimeUtc, endTimeUtc: shift.endTimeUtc, order: shift.order };
    if (shift.employeeType === 'doctor') return doctorPlateStyle(doctorBullpenColor(ns));
    if (shift.employeeType === 'nurse') return crnaPlateStyle(crnaNameplateColor(ns));
    const emp = getEmployeeById(shift.employeeId);
    return crnaPlateStyle(studentColorFor(shift.employeeType, emp?.studentCompetency));
  };

  // Students are matched explicitly on the nurse-student-* prefix; the CRNA pane is deliberately the
  // CATCH-ALL. If a new employeeType ever appears it lands there, visibly, rather than matching none of
  // the three filters and vanishing from the page entirely.
  const isStudent = (t?: string) => typeof t === 'string' && t.startsWith('nurse-student');
  const doctorShifts = filteredShifts.filter(sh => sh.employeeType === 'doctor');
  const studentShifts = filteredShifts.filter(sh => isStudent(sh.employeeType));
  const crnaShifts = filteredShifts.filter(sh => sh.employeeType !== 'doctor' && !isStudent(sh.employeeType));

  /**
   * A pane renders ONLY if it has rows, so the layout follows whoever is actually on the day:
   * docs only -> one pane; docs + CRNAs -> two; docs + CRNAs + students -> three; CRNAs + students
   * -> two. Driven off a single filtered list rather than three separate guards, so a category added
   * later cannot forget its condition.
   */
  /**
   * Shift workers first by when they leave, then pecking order ascending -- the direction the bullpen
   * and the header coins already read. Last name breaks ties, which is NOT cosmetic: pediatric pecking
   * doctors have their own #1..N sequence, so a day really does hold several #1s, and without a
   * tiebreaker they would fall back to array order and shuffle between loads.
   */
  const sortPane = (rows: any[]) => [...rows].sort((a, b) => {
    const ka = paneRowSortKey(a.order, a.endTimeUtc);
    const kb = paneRowSortKey(b.order, b.endTimeUtc);
    if (ka.group !== kb.group) return ka.group - kb.group;
    if (ka.key !== kb.key) return ka.key - kb.key;
    const ea = getEmployeeById(a.employeeId);
    const eb = getEmployeeById(b.employeeId);
    return `${ea?.lastName ?? ''} ${ea?.firstName ?? ''}`.trim()
      .localeCompare(`${eb?.lastName ?? ''} ${eb?.firstName ?? ''}`.trim());
  });

  const panes = [
    { heading: 'Anesthesiologists', rows: sortPane(doctorShifts) },
    { heading: 'CRNAs', rows: sortPane(crnaShifts) },
    { heading: 'Students', rows: sortPane(studentShifts) },
  ].filter(pane => pane.rows.length > 0);

  const Pane: React.FC<{ heading: string; rows: any[] }> = ({ heading, rows }) => (
    <div style={{ flex: '1 1 200px', minWidth: '186px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid var(--border-subtle, var(--text-faint))',
      }}>
        <span style={{
          fontSize: '0.64em', fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase',
          color: 'var(--text-faint)', fontFamily: 'var(--font-display)',
        }}>{heading}</span>
        <span style={{ fontSize: '0.72em', color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
          {rows.length}
        </span>
      </div>
      <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {rows.map((shift, i) => {
          const employee = getEmployeeById(shift.employeeId);
          const name = getEmployeeName(shift.employeeId);
          const window = `${formatTime(shift.startTimeUtc)}-${formatTime(shift.endTimeUtc)}`;
          const plate = (
            <span style={{ ...plateFor(shift), display: 'inline-block' }}>
              {name}
              {shift.order ? ` #${shift.order}` : ''}
            </span>
          );
          return (
            <div key={`${shift.employeeId}-${i}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              {employee ? (
                <EmployeeDetailsPopover
                  employee={employee}
                  hospitalName={employee.homeHospitalId ? getHospitalName(employee.homeHospitalId) : undefined}
                  shift={{ startTimeUtc: shift.startTimeUtc, endTimeUtc: shift.endTimeUtc, order: shift.order }}
                >
                  {plate}
                </EmployeeDetailsPopover>
              ) : plate}
              <span style={{ fontSize: '0.74em', color: 'var(--text-faint)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {window}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isAdmin && showUpload && (
            <>
              <button
                onClick={handleUploadButtonClick}
                disabled={uploading || !selectedDate}
                style={{
                  padding: '6px 12px',
                  fontSize: '0.85em',
                  backgroundColor: uploading ? 'var(--bg-surface-hover)' : 'var(--success)',
                  color: uploading ? 'var(--text-muted)' : 'var(--labcoat)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: uploading || !selectedDate ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!uploading && selectedDate) {
                    e.currentTarget.style.backgroundColor = '#128a3e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploading && selectedDate) {
                    e.currentTarget.style.backgroundColor = 'var(--success)';
                  }
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Spreadsheet'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.ods"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </>
          )}
          {isAdmin && showDeleteAll && !externallyProvided && (
            <button
              onClick={handleDeleteAll}
              disabled={deleting || !selectedDate || filteredShifts.length === 0}
              aria-label="Delete All Shifts"
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                backgroundColor: deleting || !selectedDate || filteredShifts.length === 0 ? 'var(--bg-surface-hover)' : 'var(--danger)',
                color: deleting || !selectedDate || filteredShifts.length === 0 ? 'var(--text-muted)' : 'var(--labcoat)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: deleting || !selectedDate || filteredShifts.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!deleting && selectedDate && filteredShifts.length > 0) {
                  e.currentTarget.style.backgroundColor = '#b8412d';
                }
              }}
              onMouseLeave={(e) => {
                if (!deleting && selectedDate && filteredShifts.length > 0) {
                  e.currentTarget.style.backgroundColor = 'var(--danger)';
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Delete All Shifts'}
            </button>
          )}
          <span style={{
            fontSize: '0.9em',
            color: 'var(--text-secondary)',
            padding: '4px 8px',
            backgroundColor: 'var(--bg-input)',
            borderRadius: '12px'
          }}>
            {filteredShifts.length} shift{filteredShifts.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {uploadResult && (
        <div style={{
          marginBottom: '15px',
          padding: '12px',
          borderRadius: '6px',
          backgroundColor: uploadResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.18)' : 'rgba(217, 119, 6, 0.18)',
          border: `1px solid ${uploadResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.4)' : 'rgba(217, 119, 6, 0.4)'}`,
          color: uploadResult.errors.length === 0 ? 'var(--success-text)' : 'var(--warning-text)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {uploadResult.message}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <div>Created: {uploadResult.shiftsCreated} shifts</div>
            <div>Parsed: {uploadResult.totalParsed} shifts</div>
            {uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Errors ({uploadResult.errors.length}):</strong>
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  {uploadResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx} style={{ fontSize: '0.85em' }}>
                      Cell {err.location}: {err.name} ({err.window}) - {err.error}
                    </li>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <li style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                      ... and {uploadResult.errors.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <button
            onClick={() => setUploadResult(null)}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '0.8em',
              backgroundColor: 'transparent',
              color: 'inherit',
              border: '1px solid currentColor',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {deleteResult && (
        <div
          role="status"
          style={{
            marginBottom: '15px',
            padding: '12px',
            borderRadius: '6px',
            backgroundColor: 'var(--success-soft)',
            border: '1px solid rgba(22, 163, 74, 0.4)',
            color: 'var(--success-text)'
          }}
        >
          <div style={{ fontWeight: 'bold' }}>{deleteResult}</div>
          <button
            onClick={() => setDeleteResult(null)}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '0.8em',
              backgroundColor: 'transparent',
              color: 'inherit',
              border: '1px solid currentColor',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {isLoading ? (
        <p style={{ margin: '10px 0', fontStyle: 'italic', color: 'var(--text-secondary)' }}>Loading shifts...</p>
      ) : (
        <>
          {showSearch && (
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <input
                type="text"
                id="filterEmployee"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                placeholder="🔍 Search by employee name..."
                className="form-control"
                style={{ fontSize: '0.9em' }}
              />
            </div>
          )}
          
          {gridView ? (
            <StaffingGrid selectedDate={selectedDate} nameFilter={filterEmployee} refreshKey={refreshKey} />
          ) : splitByType ? (
            panes.length === 0 ? (
              <span style={{ fontSize: '0.85em', color: 'var(--text-faint)', fontStyle: 'italic' }}>
                No shifts on this day.
              </span>
            ) : (
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {panes.map(pane => <Pane key={pane.heading} heading={pane.heading} rows={pane.rows} />)}
              </div>
            )
          ) : (
          <div className="shifts-table" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table style={{ fontSize: '0.85em' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px 4px' }}>Employee</th>
                  <th style={{ padding: '8px 4px' }}>Type</th>
                  <th style={{ padding: '8px 4px' }}>Home Hospital</th>
                  <th style={{ padding: '8px 4px' }}>Start</th>
                  <th style={{ padding: '8px 4px' }}>End</th>
                  {filteredShifts.some(shift => shift.employeeType === 'doctor' && shift.order) && (
                    <th style={{ padding: '8px 4px' }}>Order</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredShifts.map((shift, index) => {
                  const isBusyDoctor = busyDoctors.includes(shift.employeeId);
                  const nameStyle = isBusyDoctor ? { color: '#d97706', fontWeight: 600 } : {};
                  const employee = getEmployeeById(shift.employeeId);

                  return (
                    <tr key={index}>
                      <td style={{ padding: '6px 4px', fontWeight: '500', ...nameStyle }}>
                        {employee ? (
                          <EmployeeDetailsPopover
                            employee={employee}
                            hospitalName={employee.homeHospitalId ? getHospitalName(employee.homeHospitalId) : undefined}
                            shift={{
                              startTimeUtc: shift.startTimeUtc,
                              endTimeUtc: shift.endTimeUtc,
                              order: shift.order
                            }}
                          >
                            {getEmployeeName(shift.employeeId)}
                          </EmployeeDetailsPopover>
                        ) : (
                          getEmployeeName(shift.employeeId)
                        )}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <span style={{
                          fontSize: '0.8em',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          backgroundColor: shift.employeeType === 'doctor' ? 'rgba(74, 159, 212, 0.16)' :
                                         shift.employeeType === 'nurse' ? 'rgba(217, 119, 6, 0.16)' : 'rgba(22, 163, 74, 0.16)',
                          color: shift.employeeType === 'doctor' ? 'var(--or-blue-mid)' :
                                 shift.employeeType === 'nurse' ? 'var(--amber)' : 'var(--success-text)'
                        }}>
                          {getEmployeeTypeDisplayName(shift.employeeType)}
                        </span>
                      </td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>
                        {employee?.homeHospitalId ? (getHospitalName(employee.homeHospitalId) ?? '—') : '—'}
                      </td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>{formatTime(shift.startTimeUtc)}</td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>{formatTime(shift.endTimeUtc)}</td>
                      {shift.employeeType === 'doctor' && shift.order && (
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <span style={{
                            backgroundColor: 'rgba(217, 119, 6, 0.18)',
                            color: 'var(--amber)',
                            padding: '2px 6px',
                            borderRadius: '50%',
                            fontWeight: 'bold',
                            fontSize: '0.8em'
                          }}>
                            {shift.order}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShiftsCard;
